import React, { useRef } from "react";
import { Row, Col, Button, Badge } from "react-bootstrap";
import { CargaMasiva } from "models/ECF/CargaMasiva";
import { CargaMasivaManager } from "managers";
import FileUploader from "components/FileUploader/FileUploader";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { useTheme } from "hooks/useTheme";

/**
 * Paso 2: Descarga de plantilla y carga del archivo Excel.
 * Props:
 *   entidad        - value de la entidad seleccionada (e.g. "Item")
 *   onArchivoCargado(filas) - callback con las filas parseadas desde el servidor
 *   showLoading / hideLoading
 */
const PlantillaStep = ({ entidad, onArchivoCargado, showLoading, hideLoading }) => {
  const { handleException } = useExceptionHandler();
  const { Warning } = useMessage();
  const { theme } = useTheme();
  const archivoRef = useRef(null);
  const config = CargaMasiva.getConfigEntidad(entidad);

  const handleDescargarPlantilla = () => {
    showLoading && showLoading();
    const link = document.createElement("a");
    link.target = "_blank";
    CargaMasivaManager.descargarPlantilla(entidad)
      .then((blob) => {
        link.href = URL.createObjectURL(blob);
        link.download = `CargaMasiva_${entidad}.xls`;
        link.click();
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading && hideLoading());
  };

  const handleArchivoSeleccionado = (files) => {
    if (!files || files.length === 0) return;
    const archivo = files[0];

    const ext = archivo.name.split(".").pop().toLowerCase();
    if (ext !== "xls" && ext !== "xlsx") {
      Warning({ message: "Solo se aceptan archivos Excel (.xls o .xlsx)" });
      return;
    }

    showLoading && showLoading();
    CargaMasivaManager.procesarArchivo(entidad, archivo)
      .then((filas) => {
        if (!filas || filas.length === 0) {
          Warning({ message: "El archivo no contiene filas de datos." });
          return;
        }
        onArchivoCargado(filas);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading && hideLoading());
  };

  const stepCircle = (numero) => (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: theme.color1,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.8rem",
        flexShrink: 0,
        marginRight: 10,
      }}
    >
      {numero}
    </div>
  );

  return (
    <div>
      {/* Strip de info de la entidad */}
      <div
        className="d-flex align-items-center flex-wrap gap-2 mb-4 px-3 py-2"
        style={{
          background: "#f8f9fa",
          borderRadius: 6,
          borderLeft: `4px solid ${theme.color1}`,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "0.88rem" }}>
          {config ? config.label : entidad}
        </span>
        <span className="text-muted" style={{ fontSize: "0.8rem" }}>— Columnas:</span>
        {config &&
          config.campos.map((c) => (
            <Badge
              key={c.key}
              bg={c.required ? "secondary" : "light"}
              text={c.required ? undefined : "muted"}
              style={{ fontWeight: 400, fontSize: "0.75rem", border: c.required ? undefined : "1px solid #dee2e6" }}
            >
              {c.label}
              {c.required && <span style={{ color: "#fca5a5", marginLeft: 2 }}>*</span>}
            </Badge>
          ))}
      </div>

      {/* Cards de pasos */}
      <Row className="g-3 align-items-stretch">
        {/* Paso 1: Descargar */}
        <Col md={5}>
          <div
            className="h-100 p-4"
            style={{ border: "1px solid #dee2e6", borderRadius: 8 }}
          >
            <div className="d-flex align-items-center mb-3">
              {stepCircle(1)}
              <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Descarga la plantilla
              </span>
            </div>
            <p className="text-muted mb-4" style={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
              Descarga el archivo Excel con las columnas requeridas y complétalo con
              tus datos. La fila 1 es el encabezado y la fila 2 es un ejemplo orientativo.
            </p>
            <Button
              onClick={handleDescargarPlantilla}
              style={{
                backgroundColor: "transparent",
                borderColor: theme.color1,
                color: theme.color1,
              }}
            >
              <i className="fas fa-file-download me-2" />
              Descargar {entidad}.xls
            </Button>
          </div>
        </Col>

        {/* Flecha separadora */}
        <Col
          md={2}
          className="d-none d-md-flex align-items-center justify-content-center"
        >
          <i
            className="fas fa-arrow-right"
            style={{ fontSize: "1.3rem", color: "#adb5bd" }}
          />
        </Col>

        {/* Paso 2: Subir */}
        <Col md={5}>
          <div
            className="h-100 p-4"
            style={{ border: "1px solid #dee2e6", borderRadius: 8 }}
          >
            <div className="d-flex align-items-center mb-3">
              {stepCircle(2)}
              <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Carga el archivo completado
              </span>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
              Selecciona el archivo Excel que completaste. Se aceptan los formatos{" "}
              <strong>.xls</strong> y <strong>.xlsx</strong>.
            </p>
            <div style={{ "--file-uploader-color": theme.color1 }}>
              <FileUploader
                ref={archivoRef}
                label="Seleccionar archivo Excel"
                updateFilesCb={handleArchivoSeleccionado}
                maxSize={10 * 1024 * 1024}
                multiple={false}
                documentType="Document"
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PlantillaStep;
